// Shift PDP — v3 rebuild. Buy module state (tier selection, stepper, Card
// accessory, price math, included-card tabs) plus the gallery.
//
// The actual checkout submission — the block marked "VERBATIM BUY HANDLER"
// below — is transplanted from the old sections/product-shift.liquid inline
// <script>: clear -> add -> checkout, the Clarity event, the double-submit
// lock, the spinner, the inline amber error, the bfcache handlers, all
// unchanged except for two deliberate deviations. First: the `items` array,
// extended from a hardcoded single-item array to optionally carry a second
// line for the Card accessory (a real feature named in the same build brief
// this handler shipped with). Second (FIX-PLAN-2026-09-11 #14, Vlad
// 2026-09-11): the non-preorder path no longer clears the cart first - it
// reads /cart.js and sends one /cart/update.js instead, so gear already in
// the cart survives a Shift purchase. The preorder path (dormant - no
// preorder_plan_id is configured) is untouched, still clear -> add, since
// update.js can't attach a new selling_plan to a line it creates. Do not
// rewrite anything else in this block, do not route it through the cart
// drawer. Everything above this block is new v3 UI code whose only job is
// to keep #ProductQty and the main Buy Now button's href correct before
// the handler ever runs.
(function () {
  'use strict';

  var section = document.getElementById('product-section');
  if (!section) return;

  var SETTINGS = window.__PDP_SETTINGS__ || {};

  // ---- elements shared with the buy handler ------------------------------
  var qtyInput = document.getElementById('ProductQty');
  var buyWrap = section.querySelector('.ps__buy-wrap');
  var buyErrorEl = buyWrap ? buyWrap.querySelector('.ps__buy-error') : null;
  var buyInFlight = false;
  var buyFailCount = 0;

  // ==================== TIER SELECTION + PRICING ====================
  var tierCards = Array.from(section.querySelectorAll('[data-pdp-tier]'));
  var stepperInput = section.querySelector('[data-pdp-stepper-value]');
  var stepperMinusBtn = section.querySelector('[data-pdp-stepper-minus]');
  var stepperPlusBtn = section.querySelector('[data-pdp-stepper-plus]');
  var cardCheckbox = section.querySelector('[data-pdp-card-toggle]');

  var basePriceCents = parseInt(SETTINGS.basePriceCents, 10) || 0;
  var tier2Discount = parseFloat(SETTINGS.tier2DiscountPct) / 100 || 0;
  var tier3Discount = parseFloat(SETTINGS.tier3DiscountPct) / 100 || 0;
  var cardPriceCents = parseInt(SETTINGS.cardPriceCents, 10) || 0;
  var cardComparePriceCents = parseInt(SETTINGS.cardComparePriceCents, 10) || 0;
  var cardVariantId = SETTINGS.cardVariantId || null;
  var variantId = buyWrap ? buyWrap.dataset.variantId : null;
  var shiftProductId = SETTINGS.productId || null;

  // Merchant-authored badge text (e.g. "SAVE __AMOUNT__") is captured once,
  // before anything mutates it, so the real currency-formatted savings can
  // be substituted in on every refresh - a hardcoded "$10" would be wrong
  // for every non-USD storefront.
  var badgeTemplates = tierCards.map(function (card) {
    var el = card.querySelector('[data-pdp-tier-badge]');
    return el ? el.textContent : '';
  });

  var state = {
    tier: 1, // 1, 2 or 3
    stepperQty: 3,
    cardAdded: false,
  };

  function formatMoney(cents) {
    var currency = (window.Shopify && Shopify.currency && Shopify.currency.active) || 'PLN';
    var locale = currency === 'PLN' ? 'pl-PL' : navigator.language || 'en-US';
    return (cents / 100).toLocaleString(locale, {
      style: 'currency',
      currency: currency,
      currencyDisplay: 'narrowSymbol',
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    });
  }

  function puckQty() {
    if (state.tier === 1) return 1;
    if (state.tier === 2) return 2;
    return Math.max(3, state.stepperQty);
  }

  function tierComparePriceCents(qty) {
    return basePriceCents * qty;
  }

  function tierSalePriceCents(qty) {
    if (state.tier === 1) return basePriceCents;
    var discount = state.tier === 2 ? tier2Discount : tier3Discount;
    return Math.round(basePriceCents * qty * (1 - discount));
  }

  function cardLineCents() {
    if (!state.cardAdded) return 0;
    // Bundled with any puck tier -> the accessory's own compare/sale
    // pricing (set on the Card product itself); standalone-only pricing
    // isn't reachable through this UI since a puck tier is always selected.
    return cardComparePriceCents > 0 ? cardPriceCents : cardPriceCents;
  }

  function stickerCount() {
    return puckQty() + (state.cardAdded ? 1 : 0);
  }

  function shiftPlusMonths() {
    // Per spec: 3 months whenever any puck is present, 1 month for a
    // card-alone order. The card-alone path has no UI entry point here
    // (the accessory is an add-on checkbox, never selectable on its own),
    // so this always resolves to 3 in practice - kept for completeness.
    return puckQty() > 0 ? 3 : 1;
  }

  function updateTierCardUI() {
    tierCards.forEach(function (card) {
      var idx = parseInt(card.dataset.pdpTier, 10);
      card.classList.toggle('is-selected', idx === state.tier);
      card.setAttribute('aria-pressed', idx === state.tier ? 'true' : 'false');
    });
  }

  function updateStepperUI() {
    if (!stepperInput) return;
    stepperInput.textContent = state.stepperQty;
    if (stepperMinusBtn) stepperMinusBtn.disabled = state.stepperQty <= 3;
  }

  function updatePriceDisplays() {
    tierCards.forEach(function (card, cardIdx) {
      var idx = parseInt(card.dataset.pdpTier, 10);
      var priceEl = card.querySelector('[data-pdp-tier-price]');
      var compareEl = card.querySelector('[data-pdp-tier-compare]');
      var badgeEl = card.querySelector('[data-pdp-tier-badge]');
      if (!priceEl) return;
      var qty = idx === 3 ? Math.max(3, state.stepperQty) : idx;
      var compare = tierComparePriceCents(qty);
      var sale =
        idx === 1
          ? basePriceCents
          : Math.round(basePriceCents * qty * (1 - (idx === 2 ? tier2Discount : tier3Discount)));
      priceEl.textContent = formatMoney(sale);
      if (compareEl) {
        if (idx === 1) {
          compareEl.textContent = '';
          compareEl.hidden = true;
        } else {
          compareEl.textContent = formatMoney(compare);
          compareEl.hidden = false;
        }
      }
      if (badgeEl && badgeTemplates[cardIdx]) {
        badgeEl.textContent = badgeTemplates[cardIdx].replace('__AMOUNT__', formatMoney(compare - sale));
      }
    });

    var cardPriceEl = section.querySelector('[data-pdp-card-price]');
    var cardCompareEl = section.querySelector('[data-pdp-card-compare]');
    if (cardPriceEl) cardPriceEl.textContent = formatMoney(cardPriceCents);
    if (cardCompareEl) {
      cardCompareEl.hidden = cardComparePriceCents <= cardPriceCents;
      cardCompareEl.textContent = formatMoney(cardComparePriceCents);
    }

    var totalCents = tierSalePriceCents(puckQty()) + cardLineCents();
    var buyPriceEls = section.querySelectorAll('[data-pdp-buy-price]');
    buyPriceEls.forEach(function (el) {
      el.textContent = formatMoney(totalCents);
    });

    return totalCents;
  }

  function updateQuantityAndUrl() {
    var qty = puckQty();
    if (qtyInput) qtyInput.value = qty;
    if (!buyWrap) return;
    var mainBtn = buyWrap.querySelector('a.c-btn:not([data-preorder])');
    if (!mainBtn || !variantId) return;
    var cartPath = variantId + ':' + qty;
    if (state.cardAdded && cardVariantId) {
      cartPath += ',' + cardVariantId + ':1';
    }
    mainBtn.href = '/cart/' + cartPath + '?return_to=/checkout';
  }

  function syncStickyBar() {
    document.dispatchEvent(
      new CustomEvent('pdp:selection-changed', {
        detail: {
          puckQty: puckQty(),
          cardAdded: state.cardAdded,
          stickers: stickerCount(),
          shiftPlusMonths: shiftPlusMonths(),
          totalCents: tierSalePriceCents(puckQty()) + cardLineCents(),
        },
      }),
    );
  }

  function refresh() {
    updateTierCardUI();
    updateStepperUI();
    updatePriceDisplays();
    updateQuantityAndUrl();
    syncStickyBar();
  }

  tierCards.forEach(function (card) {
    card.addEventListener('click', function (e) {
      // The stepper buttons live inside the 3+ card; let them handle their
      // own clicks without also re-triggering tier selection weirdly (it's
      // harmless either way since both end on tier 3, but keep intent clear).
      if (e.target.closest('[data-pdp-stepper-minus], [data-pdp-stepper-plus]')) return;
      state.tier = parseInt(card.dataset.pdpTier, 10);
      refresh();
    });

    // The card is a div[role="radio"], not a native <button> (it nests real
    // <button> stepper controls, which a <button> can't legally contain) -
    // so Enter/Space activation needs wiring by hand.
    card.addEventListener('keydown', function (e) {
      if (e.key !== 'Enter' && e.key !== ' ') return;
      e.preventDefault();
      state.tier = parseInt(card.dataset.pdpTier, 10);
      refresh();
    });
  });

  if (stepperMinusBtn) {
    stepperMinusBtn.addEventListener('click', function (e) {
      e.stopPropagation();
      state.tier = 3;
      state.stepperQty = Math.max(3, state.stepperQty - 1);
      refresh();
    });
  }
  if (stepperPlusBtn) {
    stepperPlusBtn.addEventListener('click', function (e) {
      e.stopPropagation();
      state.tier = 3;
      state.stepperQty = Math.min(20, state.stepperQty + 1);
      refresh();
    });
  }

  if (cardCheckbox) {
    cardCheckbox.addEventListener('change', function () {
      state.cardAdded = cardCheckbox.checked;
      refresh();
    });
  }

  refresh();

  // ==================== INCLUDED CARD — TWO TABS ====================
  var includedTabs = Array.from(section.querySelectorAll('[data-pdp-included-tab]'));
  var includedPanels = Array.from(section.querySelectorAll('[data-pdp-included-panel]'));

  includedTabs.forEach(function (tab) {
    tab.addEventListener('click', function () {
      var target = tab.getAttribute('data-pdp-included-tab');
      includedTabs.forEach(function (t) {
        var isActive = t === tab;
        t.classList.toggle('is-active', isActive);
        t.setAttribute('aria-selected', isActive ? 'true' : 'false');
      });
      includedPanels.forEach(function (panel) {
        panel.hidden = panel.getAttribute('data-pdp-included-panel') !== target;
      });
    });
  });

  // ==================== GALLERY ====================
  var gallery = section.querySelector('[data-pdp-gallery]');
  var slides = gallery ? Array.from(gallery.querySelectorAll('[data-pdp-slide]')) : [];
  var thumbs = Array.from(section.querySelectorAll('[data-pdp-thumb]'));
  var dots = Array.from(section.querySelectorAll('[data-pdp-dot]'));
  var prevBtn = section.querySelector('[data-pdp-gallery-prev]');
  var nextBtn = section.querySelector('[data-pdp-gallery-next]');
  var currentSlide = 0;

  function setActiveSlideUI(idx) {
    thumbs.forEach(function (t, i) {
      t.classList.toggle('is-active', i === idx);
    });
    dots.forEach(function (d, i) {
      d.classList.toggle('is-active', i === idx);
    });
  }

  function goToSlide(idx) {
    if (!gallery || slides.length === 0) return;
    idx = Math.max(0, Math.min(slides.length - 1, idx));
    currentSlide = idx;
    var slideWidth = slides[0].offsetWidth;
    gallery.scrollTo({ left: slideWidth * idx, behavior: 'smooth' });
    setActiveSlideUI(idx);
  }

  if (prevBtn) prevBtn.addEventListener('click', function () { goToSlide(currentSlide - 1); });
  if (nextBtn) nextBtn.addEventListener('click', function () { goToSlide(currentSlide + 1); });

  thumbs.forEach(function (thumb, idx) {
    thumb.addEventListener('click', function () { goToSlide(idx); });
  });
  dots.forEach(function (dot, idx) {
    dot.addEventListener('click', function () { goToSlide(idx); });
  });

  if (gallery) {
    var scrollTimer;
    gallery.addEventListener(
      'scroll',
      function () {
        window.clearTimeout(scrollTimer);
        scrollTimer = window.setTimeout(function () {
          if (slides.length === 0) return;
          var slideWidth = slides[0].offsetWidth || 1;
          currentSlide = Math.round(gallery.scrollLeft / slideWidth);
          setActiveSlideUI(currentSlide);
        }, 80);
      },
      { passive: true },
    );
  }

  // ==================== VERBATIM BUY HANDLER ====================
  // Transplanted byte-for-byte from sections/product-shift.liquid. Do not
  // rewrite, do not "improve", do not route through the cart drawer.
  // Changed once, on purpose, on 2026-09-11 (FIX-PLAN-2026-09-11 #14): the
  // non-preorder path's fetch sequence only - everything else in this
  // block (this function, the lock/spinner/error state, the bfcache
  // handlers, the Clarity calls) is untouched.
  function setBuyBusy(button, busy) {
    if (!buyWrap) return;
    if (busy) {
      buyWrap.setAttribute('aria-busy', 'true');
    } else {
      buyWrap.removeAttribute('aria-busy');
    }
    if (button) {
      if (busy) {
        button.setAttribute('disabled', 'true');
        button.setAttribute('aria-disabled', 'true');
      } else {
        button.removeAttribute('disabled');
        button.removeAttribute('aria-disabled');
      }
    }
  }

  function showBuyError() {
    if (!buyErrorEl) return;
    buyErrorEl.textContent = "Didn't go through. Try again.";
    if (buyFailCount >= 2) {
      buyErrorEl.appendChild(document.createTextNode(' '));
      var link = document.createElement('a');
      link.href = '/products/shift';
      link.textContent = 'Reload';
      buyErrorEl.appendChild(link);
    }
    buyErrorEl.classList.add('is-visible');
  }

  function resetBuyState() {
    buyInFlight = false;
    buyFailCount = 0;
    if (buyWrap) {
      buyWrap.removeAttribute('aria-busy');
      var buttons = buyWrap.querySelectorAll('a.c-btn');
      for (var i = 0; i < buttons.length; i++) {
        buttons[i].removeAttribute('disabled');
        buttons[i].removeAttribute('aria-disabled');
      }
    }
    if (buyErrorEl) {
      buyErrorEl.classList.remove('is-visible');
      buyErrorEl.textContent = '';
    }
  }

  window.addEventListener('pageshow', function (e) {
    if (e.persisted) resetBuyState();
  });
  window.addEventListener('pagehide', function () {
    resetBuyState();
  });

  if (buyWrap) {
    buyWrap.addEventListener(
      'click',
      function (e) {
        var a = e.target.closest('a.c-btn');
        if (!a) return;

        if (buyInFlight) {
          e.preventDefault();
          e.stopPropagation();
          return;
        }

        e.preventDefault();
        e.stopPropagation();

        var variantId = buyWrap.dataset.variantId;
        var planId = buyWrap.dataset.preorderPlanId || null;
        var qty = parseInt(qtyInput.value, 10) || 1;
        var isPreorder =
          (a.href && a.href.indexOf('selling_plan=') !== -1) || (planId && /pre-?order/i.test(a.textContent || ''));

        var item = { id: Number(variantId), quantity: qty };
        if (isPreorder && planId) item.selling_plan = Number(planId);

        var items = [item];
        if (state.cardAdded && cardVariantId) {
          items.push({ id: Number(cardVariantId), quantity: 1 });
        }

        buyInFlight = true;
        setBuyBusy(a, true);
        if (buyErrorEl) buyErrorEl.classList.remove('is-visible');

        function onBuySuccess() {
          buyFailCount = 0;
          if (typeof window.clarity === 'function') {
            window.clarity('event', 'add_to_cart');
          }
          window.location.href = '/checkout';
        }

        function onBuyFailure(err) {
          console.error(err);
          buyFailCount += 1;
          buyInFlight = false;
          setBuyBusy(a, false);
          showBuyError();
        }

        if (isPreorder && planId) {
          // Preorder keeps the original clear -> add sequence: /cart/
          // update.js has no way to attach a NEW selling_plan to a line it
          // creates, so it can't correctly price a preorder that isn't
          // already in the cart. (Dormant today - no preorder_plan_id is
          // configured - kept so this doesn't silently break if one is.)
          fetch('/cart/clear.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'same-origin',
          })
            .then(function () {
              return fetch('/cart/add.js', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
                body: JSON.stringify({ items: items }),
                credentials: 'same-origin',
              });
            })
            .then(function (response) {
              if (!response.ok) {
                return response.text().then(function (bodyText) {
                  throw new Error('Add to cart failed: ' + response.status + ' ' + bodyText);
                });
              }
              return response.json();
            })
            .then(onBuySuccess)
            .catch(onBuyFailure);
        } else {
          // Bug #14 (FIX-PLAN-2026-09-11, decision #9, Vlad 2026-09-11):
          // "if the user already added something to the cart, it shouldn't
          // empty the cart." Replaces clear -> add with one /cart/update.js
          // call: read the current cart, set the chosen Shift variant to
          // the chosen quantity (absolute) and any OTHER existing Shift
          // line to 0, set the Card to max(existing qty, 1) if ticked -
          // nothing else in the cart is touched. On failure nothing has
          // changed, so the existing error path is enough.
          //
          // Existing lines are addressed by their own `key` (not a bare
          // variant id) wherever one already exists, since /cart/update.js
          // accepts either but a request that named the SAME line both
          // ways would be ambiguous. A bare variant id is only used to
          // upsert a line that doesn't exist yet (documented Shopify
          // behaviour: updates keyed by variant id add a new line if none
          // matches) - safe here precisely because no other instruction in
          // the same request also targets that not-yet-existing line.
          fetch('/cart.js', { headers: { Accept: 'application/json' }, credentials: 'same-origin' })
            .then(function (res) {
              return res.json();
            })
            .then(function (cart) {
              var updates = {};
              var shiftLine = null;

              cart.items.forEach(function (it) {
                if (shiftProductId && it.product_id === shiftProductId) {
                  if (!shiftLine && it.variant_id === Number(variantId)) {
                    shiftLine = it;
                  } else {
                    updates[it.key] = 0;
                  }
                }
              });

              if (shiftLine) {
                updates[shiftLine.key] = qty;
              } else {
                updates[String(variantId)] = qty;
              }

              if (state.cardAdded && cardVariantId) {
                var cardLine = cart.items.find(function (it) {
                  return it.variant_id === Number(cardVariantId);
                });
                if (cardLine) {
                  updates[cardLine.key] = Math.max(cardLine.quantity, 1);
                } else {
                  updates[String(cardVariantId)] = 1;
                }
              }

              return fetch('/cart/update.js', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
                body: JSON.stringify({ updates: updates }),
                credentials: 'same-origin',
              });
            })
            .then(function (response) {
              if (!response.ok) {
                return response.text().then(function (bodyText) {
                  throw new Error('Cart update failed: ' + response.status + ' ' + bodyText);
                });
              }
              return response.json();
            })
            .then(onBuySuccess)
            .catch(onBuyFailure);
        }
      },
      true,
    );
  }
  // ==================== END VERBATIM BUY HANDLER ====================
})();
